/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CompanyTable } from './CompanyTable';
import Loader from '../../components/loader';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import { getSponsoredInitiatives, getSponsorshipCodes } from '../../actions/portfolio-tracker';
import { AdminBreadcrumbs } from '../admin-dashboard/AdminBreadcrumbs';
import { useAppSelector } from '../../reducers';
import { isStaff } from '../../selectors/user';
import { convertToTableRows, SponsoredCompanyRow } from './utils';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { BasicAlert } from '@components/alert/BasicAlert';
import { generateUrl } from '../util';
import { ROUTES } from '@constants/routes';
import { ExtraFeature, FeatureStability } from '@g17eco/molecules';

export const CompanySponsorship = () => {
  const match = useRouteMatch<{ [key: string]: string }>();
  const portfolioId = match.params.portfolioId;
  const history = useHistory();

  const [sponsorshipCodes, setSponsorshipCodes] = React.useState<{ codes: string[]; loaded: boolean }>({
    codes: [],
    loaded: false,
  });
  const [selectedCode, setSelectedCode] = useState<string | undefined>();
  const [companiesState, setCompaniesState] = React.useState<{ rows: SponsoredCompanyRow[]; loaded: boolean }>({
    rows: [],
    loaded: false,
  });
  const isUserStaff = useAppSelector(isStaff);

  const handleReload = useCallback(async () => {
    if (!selectedCode) {
      return;
    }
    return getSponsoredInitiatives(portfolioId, selectedCode).then((data) =>
      setCompaniesState({ rows: convertToTableRows(data), loaded: true })
    );
  }, [portfolioId, selectedCode]);

  const handleReloadCodes = useCallback(async () => {
    setCompaniesState({ rows: [], loaded: false });
    return getSponsorshipCodes(portfolioId).then((codes) => {
      setSponsorshipCodes({ codes, loaded: true });
      if (!selectedCode || !codes.includes(selectedCode)) {
        setSelectedCode(codes[0]);
      }
    });
  }, [portfolioId, selectedCode]);

  React.useEffect(() => {
    if (!portfolioId) {
      return;
    }
    handleReloadCodes().then(() => handleReload());
  }, [portfolioId, handleReload, handleReloadCodes]);

  if (!portfolioId || !sponsorshipCodes.loaded) {
    return <Loader />;
  }

  if (!isUserStaff) {
    return (
      <Dashboard className='company-sponsorship'>
        <DashboardSection title='Coming Soon'>
          <p>Here you can view and manage the companies that have been sponsored using your discount code.</p>
        </DashboardSection>
      </Dashboard>
    );
  }

  const noCodes = sponsorshipCodes.codes.length === 0;
  const renderReferralCodeToggle = () => {
    if (noCodes) {
      return 'No referral codes';
    }
    return (
      <>
        {selectedCode ? `Referral code: ${selectedCode}` : 'Please select a code'}{' '}
        <i className='fa-light fa-chevron-down text-ThemeHeadingMedium ml-3 -mt-1' />
      </>
    );
  };

  return (
    <Dashboard className='company-sponsorship'>
      <DashboardRow>
        <AdminBreadcrumbs
          breadcrumbs={[{ label: 'Company sponsorship' }]}
          initiativeId={portfolioId}
          isPortfolioTracker={true}
        />
      </DashboardRow>
      <DashboardSectionTitle
        title={<span>Company sponsorship <FeatureStability feature={ExtraFeature.CompanySponsorship} /></span>}
        buttons={[
          <Button
            color='secondary'
            outline
            onClick={() => history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_MANAGE_COMPANIES, { portfolioId }))}
          >
            Sponsor new companies
          </Button>,
        ]}
      />
      <DashboardSection
        title={
          <UncontrolledDropdown>
            <DropdownToggle
              className='d-flex align-items-center -ms-1 px-1 fs-5 text-medium text-ThemeHeadingMedium border-0 bg-transparent'
              disabled={noCodes}
            >
              {renderReferralCodeToggle()}
            </DropdownToggle>
            <DropdownMenu>
              {sponsorshipCodes.codes.map((code) => (
                <DropdownItem key={`code-${code}`} onClick={() => setSelectedCode(code)}>
                  {code}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        }
      >
        <p>Companies that have used your referral code.</p>
        {noCodes || !selectedCode ? (
          <BasicAlert type='info'>
            You have no sponsorship referral codes. Please contact our support if you think this is an error.
          </BasicAlert>
        ) : (
          <CompanyTable
            referralCode={selectedCode}
            portfolioId={portfolioId}
            companiesState={companiesState}
            handleReload={handleReload}
          />
        )}
      </DashboardSection>
    </Dashboard>
  );
};
